import React, { useState } from "react";
import styles from "./FeedbackPage.module.css";
import { useAuth } from "../../../components/Auth/AuthProvider";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useThemedIcon } from "../../../hooks/useThemedIcon";
import loadingButtonLight from "../../../assets/loading_button_dark.svg";
import loadingButtonDark from "../../../assets/loading_button_light.svg";

interface FeedbackPageProps {
  onClose: () => void;
}

const FeedbackPage: React.FC<FeedbackPageProps> = ({ onClose }) => {
  const { getIcon } = useThemedIcon();
  const loadingButtonIcon = getIcon(loadingButtonLight, loadingButtonDark);
  const { user, userProfile } = useAuth();
  const [feedbackText, setFeedbackText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackText.trim()) return;

    setIsSending(true);
    try {
      const functions = getFunctions();
      const sendFeedbackEmail = httpsCallable(functions, "sendFeedbackEmail");

      await sendFeedbackEmail({
        feedback: feedbackText,
        userEmail: user?.email || "anonymous",
        userName:
          userProfile?.fullName || user?.displayName || "Anonymous User",
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Error sending feedback:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {submitted ? (
          <div className={styles.successState}>
            <h2 className={styles.title}>Thank you for your feedback.</h2>
            <p className={styles.description}>
              We really appreciate you taking the time to share your thoughts.
              Your feedback goes straight to the team and helps shape what we
              build next.
            </p>
            <button
              className={styles.submitAnotherButton}
              onClick={() => {
                setSubmitted(false);
                setFeedbackText("");
              }}
            >
              Submit another piece of feedback
            </button>
          </div>
        ) : (
          <>
            <h2 className={styles.title}>Help shape Noah.</h2>
            <p className={styles.description}>
              Tell us what’s working, what’s frustrating, or what you’d like to
              see next. Bugs, ideas, or quick thoughts welcome.
            </p>

            <div className={styles.textareaWrapper}>
              <textarea
                className={styles.textarea}
                placeholder="Type freely..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={!feedbackText.trim()}
              >
                Submit feedback
                {isSending && (
                  <img
                    src={loadingButtonIcon}
                    alt="Loading"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      marginLeft: "0.5rem",
                    }}
                    className={styles.spinning}
                  />
                )}
              </button>
            </div>

            <p className={styles.disclaimer}>
              Your feedback is private and only used to improve Noah.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;
